import { useCallback, useEffect, useMemo, useState } from 'react';
import useTableState from '../../hooks/useTableState';
import { Box, FormControl, Select, MenuItem, Button, Chip, IconButton } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getAllAgents, getAllAgentGroups, updateUser, impersonateUser, reactivateUser } from '../../api/userApi';
import { getCurrentTierBulk } from '../../api/tierApi';
import { getLeaderboardTitles } from '../../api/rankingsApi';
import { downloadAgreement } from '../../api/agreementApi';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { facetFilterOptions } from '../common/facetFilter';

const TIER_BADGE = {
  slate:   { bg: '#F1F5F9', fg: '#475569' },
  bronze:  { bg: '#FEF3C7', fg: '#92400E' },
  silver:  { bg: '#E5E7EB', fg: '#374151' },
  gold:    { bg: '#FEF3C7', fg: '#B45309' },
  diamond: { bg: '#DBEAFE', fg: '#1D4ED8' },
};

const ROLE_BADGES = {
  'Super Admin': { bg: '#FEE2E2', color: '#991B1B' },
  'Admin': { bg: '#FEF3C7', color: '#92400E' },
  'Accountant': { bg: '#FEF3C7', color: '#92400E' },
  'Leader': { bg: '#DBEAFE', color: '#1E40AF' },
  'Group Admin': { bg: '#DBEAFE', color: '#1E40AF' },
  'Sales Agent': { bg: '#E0E7FF', color: '#3730A3' },
  'In-house Agent': { bg: '#E0E7FF', color: '#3730A3' },
  'Referral': { bg: '#FCE7F3', color: '#9D174D' },
};

// Inactivity bands for the Last Active column. 14 days is the threshold the
// deactivation review uses, so anything at or past it reads as "stale" at a glance.
const STALE_DAYS = 14;
const WATCH_DAYS = 7;

function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  // dd/mm/yyyy — matches the rest of the app's date convention.
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtRM(v) {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return 'RM 0';
  return `RM ${n.toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
}

// An inactivity-locked user keeps Status "Active" in the database but only sees the
// "Account Inactive" screen at login, so the list shows them as Deactivated instead.
// (user 2026-09-19)
const shownStatus = (u) => (u.status === 'Active' && u.accessBlocked ? 'Deactivated' : u.status);

const ALL_ROLES = ['Super Admin', 'Admin', 'Accountant', 'Leader', 'Group Admin', 'Sales Agent', 'In-house Agent', 'Referral'];

export default function UserManagement() {
    const ts = useTableState();
  const { user: currentUser, setUser } = useUser();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [tierByUser, setTierByUser] = useState({});
  const [lbTitleByUser, setLbTitleByUser] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        getAllAgents(),
        getAllAgentGroups(),
      ]);
      const userList = usersRes.data || [];
      setUsers(userList);
      setGroups(groupsRes.data);
      // Bulk-load tiers in the background; non-blocking.
      const ids = userList.map(u => u.userId).filter(Boolean);
      if (ids.length > 0) {
        getCurrentTierBulk(ids)
          .then(r => setTierByUser(r.data || {}))
          .catch(() => setTierByUser({}));
        // Earned leaderboard titles per user → { userId: [titles] } for the export.
        getLeaderboardTitles()
          .then(r => {
            const map = {};
            (r.data || []).forEach(x => { map[x.userId] = x.titles || []; });
            setLbTitleByUser(map);
          })
          .catch(() => setLbTitleByUser({}));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredUsers = useMemo(() => {
    let result = users;
    if (statusFilter !== 'All') {
      result = result.filter(u => shownStatus(u) === statusFilter);
    }
    if (roleFilter !== 'All') {
      result = result.filter(u =>
        u.userRoles?.some(r => r.roleName === roleFilter)
      );
    }
    if (groupFilter !== 'All') {
      result = result.filter(u => u.groupName === groupFilter);
    }
    return result;
  }, [users, roleFilter, groupFilter, statusFilter]);

  const handleArchive = async (user) => {
    const newStatus = user.status === 'Active' ? 'Archived' : 'Active';
    if (!window.confirm(`${newStatus === 'Archived' ? 'Archive' : 'Restore'} ${`${user.lastName ?? ''} ${user.firstName ?? ''}`.trim() || user.alias || ''}?`)) return;
    try {
      await updateUser({
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        alias: user.alias,
        email: user.email,
        phoneNumber: user.phoneNumber,
        roleId: user.userRoles?.map(r => r.roleId) ?? [],
        groupId: 0,
        personalComm: user.personalComm ?? 0,
        downlineOverridingComm: user.downlineOverridingComm,
        teamOverridingComm: user.teamOverridingComm,
        status: newStatus,
        updatedBy: currentUser?.username ?? 'system',
      });
      fetchData();
    } catch (err) {
      console.error('Error archiving user:', err);
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Login as ${`${targetUser.lastName ?? ''} ${targetUser.firstName ?? ''}`.trim() || targetUser.alias || ''}? You will be switched to their account.`)) return;
    try {
      // Save admin's credentials for "Back to Admin"
      localStorage.setItem('AdminToken', localStorage.getItem('Token'));
      localStorage.setItem('AdminUserId', localStorage.getItem('UserId'));
      localStorage.setItem('ImpersonatingFrom', currentUser?.username);

      const res = await impersonateUser(targetUser.userId);
      const data = res.data;

      // Switch to target user
      localStorage.setItem('Token', data.token);
      localStorage.setItem('UserId', data.userId);
      setUser({
        userId: data.userId,
        username: data.username,
        roles: data.roles,
        roleRights: data.roleRights,
        groupName: data.groupName,
      });
      navigate('/dashboard');
    } catch (err) {
      alert('Failed to impersonate: ' + (err.response?.data || err.message));
    }
  };

  const handleAdd = () => {
    navigate('/admin/users/new');
  };

  const handleExportUsers = () => {
    const csvConfig = mkConfig({ filename: 'User_List_Export', fieldSeparator: ',', useKeysAsHeaders: true });
    const exportData = filteredUsers.map(u => ({
      'Name': `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.alias || '',
      'Username': u.username,
      'Email': u.email ?? '',
      'Phone': u.phoneNumber ?? '',
      'Role': u.userRoles?.map(r => r.roleName).join(', ') ?? '',
      'Group': u.groupName ?? '',
      'PSC%': u.personalComm ?? '',
      'DOC%': u.downlineOverridingComm ?? '',
      'TOC%': u.teamOverridingComm ?? '',
      'MOC%': u.managerOverridingComm ?? '',
      'Upline': (u.buddies?.length
        ? u.buddies.map(b => b.uplineName).filter(Boolean).join(', ')
        : (u.uplineName?.trim() ?? '')),
      'Manager': u.managerName?.trim() ?? '',
      'Title Ranking': tierByUser[u.userId]?.name ?? '',
      'Leaderboard Titles': (lbTitleByUser[u.userId] || []).join(', '),
      'Lifetime Sales': u.lifetimeSalesCount ?? 0,
      'Lifetime Sales Amount': u.lifetimeSalesAmount ?? 0,
      'Last Sale': fmtDate(u.lastSaleOn) ?? '',
      'Last Active': fmtDate(u.lastLoginOn) ?? 'Never',
      // Blank rather than 0 when unknown — a never-seen user is not a 0-day-idle user.
      'Inactive Days': u.inactiveDays ?? '',
      'Status': shownStatus(u),
    }));
    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  // Puts a disabled account back in play. Only Super Admin reaches this screen
  // at all (UserManagement right), so the button needs no extra role check.
  const handleReactivate = useCallback(async (u) => {
    const label = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.username;
    if (!window.confirm(`Reactivate ${label}? They will be able to log in again.`)) return;
    try {
      await reactivateUser(u.userId);
      const res = await getAllAgents();
      setUsers(res.data ?? []);
    } catch {
      alert('Could not reactivate this user.');
    }
  }, []);

  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      accessorFn: row => `${row.lastName ?? ''} ${row.firstName ?? ''}`.trim() || row.alias || '',
    },
    {
      id: 'username',
      header: 'Username',
      accessorKey: 'username',
    },
    {
      id: 'role',
      header: 'Role',
      accessorFn: row => row.userRoles?.map(r => r.roleName).join(', ') ?? '',
      Cell: ({ row }) => {
        const roles = row.original.userRoles ?? [];
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {roles.map((r, i) => {
              const badge = ROLE_BADGES[r.roleName] ?? { bg: '#F1F5F9', color: '#64748B' };
              return (
                <Chip
                  key={i}
                  label={r.roleName}
                  size="small"
                  sx={{ backgroundColor: badge.bg, color: badge.color, fontWeight: 600, fontSize: '0.7rem' }}
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      id: 'group',
      header: 'Group',
      accessorFn: row => row.groupName ?? '—',
    },
    {
      id: 'tier',
      header: 'Tier',
      accessorFn: row => tierByUser[row.userId]?.name ?? '',
      Cell: ({ row }) => {
        const t = tierByUser[row.original.userId];
        if (!t) return <span style={{ color: '#94A3B8' }}>—</span>;
        const palette = TIER_BADGE[t.badgeColor] ?? TIER_BADGE.slate;
        return (
          <Chip
            label={t.name}
            size="small"
            sx={{ backgroundColor: palette.bg, color: palette.fg, fontWeight: 600, fontSize: '0.7rem' }}
          />
        );
      },
      size: 130,
    },
    {
      id: 'psc',
      header: 'PSC',
      accessorFn: row => row.personalComm != null ? `${row.personalComm}%` : '—',
      size: 80,
    },
    {
      id: 'doc',
      header: 'DOC',
      accessorFn: row => row.downlineOverridingComm != null ? `${row.downlineOverridingComm}%` : '—',
      size: 80,
    },
    {
      id: 'toc',
      header: 'TOC',
      accessorFn: row => row.teamOverridingComm != null ? `${row.teamOverridingComm}%` : '—',
      size: 80,
    },
    {
      id: 'moc',
      header: 'MOC',
      accessorFn: row => row.managerOverridingComm != null ? `${row.managerOverridingComm}%` : '—',
      size: 80,
    },
    {
      id: 'upline',
      header: 'Upline',
      accessorFn: row => {
        const buddies = row.buddies ?? [];
        if (buddies.length > 0) {
          return buddies.map(b => b.uplineName?.trim()).filter(Boolean).join(', ') || '—';
        }
        return row.uplineName?.trim() || '—';
      },
      Cell: ({ row }) => {
        const buddies = row.original.buddies ?? [];
        if (buddies.length > 0) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {buddies.map((b, i) => (
                <Box key={i} sx={{ fontSize: '0.8rem' }}>
                  {b.uplineName?.trim() || '—'}
                  {buddies.length > 1 && (
                    <span style={{ color: '#94A3B8', marginLeft: 4, fontSize: '0.7rem' }}>
                      {Number(b.splitPercent).toFixed(0)}%
                    </span>
                  )}
                </Box>
              ))}
            </Box>
          );
        }
        return <span>{row.original.uplineName?.trim() || '—'}</span>;
      },
      size: 160,
    },
    {
      id: 'manager',
      header: 'Manager',
      accessorFn: row => row.managerName?.trim() || '—',
      size: 140,
    },
    {
      id: 'createdOn',
      header: 'Created Date',
      // GetAllUsersResponse.CreatedOn surfaces as `createdOn` on the JS side.
      accessorFn: row => row.createdOn || '',
      sortingFn: (a, b) => new Date(a.original.createdOn || 0) - new Date(b.original.createdOn || 0),
      size: 110,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (!v) return '—';
        const d = new Date(v);
        if (isNaN(d)) return '—';
        // dd/mm/yyyy — matches the rest of the app's date convention.
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}/${d.getFullYear()}`;
      },
    },
    {
      id: 'lifetimeSales',
      header: 'Lifetime Sales',
      // Sorts on value, not count, so the biggest producers rise to the top.
      accessorFn: row => row.lifetimeSalesCount ?? 0,
      sortingFn: (a, b) => (a.original.lifetimeSalesAmount ?? 0) - (b.original.lifetimeSalesAmount ?? 0),
      size: 130,
      Cell: ({ row }) => {
        const n = row.original.lifetimeSalesCount ?? 0;
        if (!n) {
          return (
            <Chip
              label="No sales"
              size="small"
              sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.7rem' }}
            />
          );
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <Box sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {n} {n === 1 ? 'sale' : 'sales'}
            </Box>
            <Box sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
              {fmtRM(row.original.lifetimeSalesAmount)}
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'lastActive',
      header: 'Last Active',
      accessorFn: row => fmtDate(row.lastLoginOn) ?? 'Never',
      // Never-seen users sort to the end in both directions rather than pretending
      // to be infinitely stale or infinitely fresh.
      sortingFn: (a, b) => {
        const ta = a.original.lastLoginOn ? new Date(a.original.lastLoginOn).getTime() : -Infinity;
        const tb = b.original.lastLoginOn ? new Date(b.original.lastLoginOn).getTime() : -Infinity;
        return ta - tb;
      },
      size: 130,
      Cell: ({ row }) => {
        const { lastLoginOn, inactiveDays } = row.original;
        const shown = fmtDate(lastLoginOn);
        if (!shown) {
          return (
            <Chip
              label="Never"
              size="small"
              sx={{ backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: '0.7rem' }}
            />
          );
        }
        const d = inactiveDays ?? 0;
        const tone = d >= STALE_DAYS
          ? { bg: '#FEE2E2', fg: '#991B1B' }
          : d >= WATCH_DAYS
            ? { bg: '#FEF3C7', fg: '#92400E' }
            : { bg: '#D1FAE5', fg: '#065F46' };
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
            <Box sx={{ fontSize: '0.8rem' }}>{shown}</Box>
            <Chip
              label={d === 0 ? 'today' : `${d}d idle`}
              size="small"
              sx={{ backgroundColor: tone.bg, color: tone.fg, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
            />
          </Box>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: shownStatus,
      Cell: ({ cell }) => {
        const val = cell.getValue();
        const isActive = val === 'Active';
        const isArchived = val === 'Archived';
        return (
          <Chip
            label={val}
            size="small"
            sx={{
              backgroundColor: isActive ? '#D1FAE5' : isArchived ? '#F1F5F9' : '#FEE2E2',
              color: isActive ? '#065F46' : isArchived ? '#64748B' : '#991B1B',
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <span
            onClick={() => navigate(`/admin/users/${row.original.userId}`)}
            style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
          >
            Edit
          </span>
          <span
            onClick={() => handleArchive(row.original)}
            style={{ color: row.original.status === 'Active' ? '#64748B' : '#10B981', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
          >
            {row.original.status === 'Active' ? 'Archive' : 'Restore'}
          </span>
          {row.original.userId !== currentUser?.userId && (
            <span
              onClick={() => handleImpersonate(row.original)}
              style={{ color: '#6366F1', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
            >
              Login As
            </span>
          )}
          <span
            onClick={async () => {
              try { await downloadAgreement(row.original.userId); }
              catch (e) {
                const status = e?.response?.status;
                alert(status === 404 ? 'No agreement on file for this user.' : 'Could not download agreement.');
              }
            }}
            style={{ color: '#0F62CC', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
            title="Download signed agreement PDF"
          >
            Agreement
          </span>
          {(row.original.status === 'Inactive' || shownStatus(row.original) === 'Deactivated') && (
            <span
              onClick={() => handleReactivate(row.original)}
              style={{ color: '#15803D', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
              title="Restore login access and restart the idle clock"
            >
              Reactivate
            </span>
          )}
          <IconButton size="small" onClick={() => navigate(`/settings/user-permissions/${row.original.userId}`)} title="Manage Permissions">
            <SecurityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      size: 280,
    },
  ], [tierByUser, currentUser, navigate, handleReactivate]);

  const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns,
    data: filteredUsers,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', showGlobalFilter: true },
    state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
    onGlobalFilterChange: ts.setGlobalFilter,
    onSortingChange: ts.setSorting,
    onPaginationChange: ts.setPagination,
    onColumnFiltersChange: ts.setColumnFilters,
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableContainerProps: { sx: { overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        if (e.target.closest('input[type="checkbox"]') || (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer')) return;
        if (e.ctrlKey || e.metaKey) {
          window.open(`/admin/users/${row.original.userId}`, '_blank');
        } else {
          navigate(`/admin/users/${row.original.userId}`);
        }
      },
      sx: { cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
    }),
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search users...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
    muiPaginationProps: {
      shape: 'rounded',
      showRowsPerPage: true,
      rowsPerPageOptions: [10, 25, 50, 100],
    },
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
            User Management
          </h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
            Manage all system users and their roles
          </p>
        </div>
        <Button
          variant="contained"
          size="small"
          onClick={handleAdd}
          sx={{
            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: { xs: '0.75rem', md: '0.85rem' },
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
          }}
        >
          + Add User
        </Button>
      </Box>

      {/* Filters Row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 120, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', backgroundColor: 'white' }}
          >
            <MenuItem value="All">All Status</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="Deactivated">Deactivated</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ minWidth: 140, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', backgroundColor: 'white' }}
          >
            <MenuItem value="All">All Roles</MenuItem>
            {ALL_ROLES.map(role => (
              <MenuItem key={role} value={role}>{role}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            sx={{ minWidth: 140, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', backgroundColor: 'white' }}
          >
            <MenuItem value="All">All Groups</MenuItem>
            {groups.map(g => (
              <MenuItem key={g.groupId} value={g.groupName}>{g.groupName}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <div style={{ flex: 1 }} />
        <Button
          variant="outlined"
          onClick={handleExportUsers}
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: '0.8rem',
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#E2E8F0',
            color: '#475569',
            '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
          }}
        >
          Export Users
        </Button>
      </Box>

      {/* Table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <MaterialReactTable table={table} />
      </div>
    </div>
  );
}
